import { Suspense } from 'react';

import type { AppRoute } from './types';

import { AdminGuard } from './guards/AdminGuard';
import { PrivateGuard } from './guards/PrivateGuard';

type GuardMap = Record<
  string,
  Record<string, Array<(props: GuardProps) => JSX.Element | null>>
>;

type GuardProps = {
  element: JSX.Element;
};

const guardMap: GuardMap = {
  permission: { admin: [AdminGuard] },
  scope: { private: [PrivateGuard] },
};

export const guardRoute = (route: AppRoute): AppRoute => {
  const { element, routes, ...rest } = route;

  const guards = Object.keys(guardMap).reduce(
    (acc, key) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const foundGuards = guardMap?.[key]?.[route?.[key]];
      if (foundGuards) {
        acc.push(...foundGuards);
        if (import.meta.env.DEV) {
          console.debug(
            `Wrapped ${route.path} with ${foundGuards
              .map((g) => g.name)
              .join(', ')} guards`
          );
        }
      }
      return acc;
    },
    [] as Array<(props: GuardProps) => JSX.Element | null>
  );

  const Wrapper = guards.reduceRight(
    (Guard, NextGuard) => (props: GuardProps) => (
      <Guard element={<NextGuard element={props.element} />} />
    ),
    ({ element }: GuardProps) => element
  );

  return {
    ...rest,
    element: <Wrapper element={<Suspense>{element}</Suspense>} />,
    routes: routes?.map(guardRoute),
  };
};
